/**
 * The minimap (ADR-0077): the whole world in a small panel — land and sea as the viewer
 * knows them, borders in their owners' colours, cities as dots — with a box for what is
 * on screen. Click or drag on it to move the view there.
 *
 * The picture of the world is redrawn only when the game changes (`refresh`); the box is
 * redrawn whenever the view moves (`drawView`), which is cheap.
 */

import type { Content } from '../../content/schema/index.js';
import { VIS_EXPLORED, VIS_UNKNOWN } from '../core/state/types.js';
import type { Scene } from '../render/scene.js';

export interface MinimapView {
  /** Corners of the screen in tile coordinates (columns and rows, fractional). */
  left: number;
  right: number;
  top: number;
  bottom: number;
}

export interface Minimap {
  refresh(scene: Scene, content: Content): void;
  drawView(view: MinimapView | null): void;
}

const UNKNOWN = '#cdc6b3';
const WIDTH = 268;

export function createMinimap(container: HTMLElement, onJump: (col: number, row: number) => void): Minimap {
  container.innerHTML = `
    <div class="mini-head"><b>World</b><span class="h-muted" id="mini-explored"></span></div>
    <div class="mini-frame"><canvas id="mini-world"></canvas><canvas id="mini-view"></canvas></div>`;
  const world = container.querySelector<HTMLCanvasElement>('#mini-world')!;
  const viewCanvas = container.querySelector<HTMLCanvasElement>('#mini-view')!;
  const explored = container.querySelector<HTMLElement>('#mini-explored')!;
  let cols = 1;
  let rows = 1;
  let cell = 1;

  const ratio = Math.min(2, window.devicePixelRatio || 1);
  const size = (c: HTMLCanvasElement, w: number, h: number): void => {
    c.width = Math.round(w * ratio);
    c.height = Math.round(h * ratio);
    c.style.width = `${w}px`;
    c.style.height = `${h}px`;
    c.getContext('2d')!.setTransform(ratio, 0, 0, ratio, 0, 0);
  };

  const jumpTo = (event: PointerEvent): void => {
    const rect = viewCanvas.getBoundingClientRect();
    const col = Math.floor((event.clientX - rect.left) / cell);
    const row = Math.floor((event.clientY - rect.top) / (cell * 0.86));
    onJump(Math.max(0, Math.min(cols - 1, col)), Math.max(0, Math.min(rows - 1, row)));
  };
  let dragging = false;
  viewCanvas.addEventListener('pointerdown', (e) => {
    dragging = true;
    viewCanvas.setPointerCapture(e.pointerId);
    jumpTo(e);
  });
  viewCanvas.addEventListener('pointermove', (e) => {
    if (dragging) jumpTo(e);
  });
  viewCanvas.addEventListener('pointerup', () => {
    dragging = false;
  });

  return {
    refresh(scene, content) {
      const map = scene.map;
      if (map.width !== cols || map.height !== rows) {
        cols = map.width;
        rows = map.height;
        cell = WIDTH / (cols + 0.5);
        const h = Math.ceil(rows * cell * 0.86 + cell * 0.3);
        size(world, WIDTH, h);
        size(viewCanvas, WIDTH, h);
      }
      const g = world.getContext('2d')!;
      g.fillStyle = UNKNOWN;
      g.fillRect(0, 0, WIDTH, world.height);
      let known = 0;
      const cities: Array<{ x: number; y: number; colour: string }> = [];
      for (let row = 0; row < rows; row++) {
        for (let col = 0; col < cols; col++) {
          const i = row * cols + col;
          const level = scene.seen(i);
          if (level === VIS_UNKNOWN) continue;
          known++;
          const x = col * cell + (row & 1 ? cell / 2 : 0);
          const y = row * cell * 0.86;
          const terrain = content.terrain.get(map.terrain[i]!);
          g.fillStyle = terrain.colour;
          g.fillRect(x, y, cell + 0.6, cell * 0.86 + 0.6);
          const owner = scene.ownerColour(i);
          if (owner) {
            g.globalAlpha = terrain.water ? 0.25 : 0.45;
            g.fillStyle = owner;
            g.fillRect(x, y, cell + 0.6, cell * 0.86 + 0.6);
            g.globalAlpha = 1;
          }
          if (level === VIS_EXPLORED) {
            g.fillStyle = 'rgba(29, 39, 48, 0.18)';
            g.fillRect(x, y, cell + 0.6, cell * 0.86 + 0.6);
          }
          const city = scene.city(i);
          if (city) cities.push({ x: x + cell / 2, y: y + cell * 0.43, colour: city.colour });
        }
      }
      for (const c of cities) {
        g.beginPath();
        g.arc(c.x, c.y, Math.max(2.2, cell * 0.9), 0, Math.PI * 2);
        g.fillStyle = '#ffffff';
        g.fill();
        g.lineWidth = 1.4;
        g.strokeStyle = c.colour;
        g.stroke();
      }
      explored.textContent = `${Math.round((known / (cols * rows)) * 100)}% explored`;
    },

    drawView(view) {
      const g = viewCanvas.getContext('2d')!;
      g.clearRect(0, 0, viewCanvas.width, viewCanvas.height);
      if (!view) return;
      const x = view.left * cell;
      const y = view.top * cell * 0.86;
      const w = Math.max(4, (view.right - view.left) * cell);
      const h = Math.max(4, (view.bottom - view.top) * cell * 0.86);
      g.lineWidth = 1.6;
      g.strokeStyle = '#1d2730';
      g.strokeRect(x, y, w, h);
      // The world wraps east–west: the box continues on the other side.
      if (x + w > WIDTH) g.strokeRect(x - cols * cell, y, w, h);
      if (x < 0) g.strokeRect(x + cols * cell, y, w, h);
    },
  };
}
